import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { ContactRow } from "./ContactBits";

export default function ClassicTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        color: "#171717",
        background: "#ffffff",
        padding: "56px 64px",
        fontSize: 10,
        lineHeight: 1.5,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <header style={{ textAlign: "center", marginBottom: 20 }}>
        <div data-resume-h1 style={{ fontWeight: 600, fontSize: 30, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {personal.fullName || "Your Name"}
        </div>
        {personal.title && (
          <div style={{ fontStyle: "italic", fontSize: 13, marginTop: 4, color: "#525252" }}>
            {personal.title}
          </div>
        )}
        <ContactRow
          personal={personal}
          size={10}
          color="#525252"
          style={{ marginTop: 8, fontSize: 10, justifyContent: "center", gap: 8 }}
        />
        <div style={{ borderBottom: "1px solid #0a0a0a", marginTop: 14 }} />
      </header>

      {summary && (
        <Section title="Professional Summary">
          <div>{summary}</div>
        </Section>
      )}

      {skills?.length > 0 && (
        <Section title="Skills">
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10 }}>{skills.join(" · ")}</div>
        </Section>
      )}

      {hasAny(experience, ["company", "role"]) && (
        <Section title="Experience">
          {experience.map((e, i) => {
            const bullets = nonEmptyBullets(e.bullets);
            if (!e.company && !e.role && bullets.length === 0) return null;
            return (
              <div key={e.id || i} data-page-item="true" style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <span data-resume-company style={{ fontWeight: 600 }}>{e.company}</span>
                    {e.location && <span style={{ color: "#525252" }}>, <span data-resume-location>{e.location}</span></span>}
                  </div>
                  <div style={{ fontStyle: "italic", fontSize: 10, color: "#525252" }}><span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span></div>
                </div>
                {e.role && <div data-resume-jobtitle style={{ fontStyle: "italic", color: "#404040" }}>{e.role}</div>}
                {bullets.length > 0 && (
                  <ul data-resume-workdesc style={{ margin: "3px 0 0 16px", padding: 0 }}>
                    {bullets.map((b, bi) => (
                      <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10 }}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {hasAny(education, ["school", "degree"]) && (
        <Section title="Education">
          {education.map((ed, i) => {
            if (!ed.school && !ed.degree) return null;
            return (
              <div key={ed.id || i} style={{ marginBottom: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{ed.school}</span>
                    {ed.degree && <span style={{ color: "#404040" }}> — {ed.degree}{ed.field ? `, ${ed.field}` : ""}</span>}
                  </div>
                  <div style={{ fontStyle: "italic", fontSize: 10, color: "#525252" }}>{fmtRange(ed.startDate, ed.endDate)}</div>
                </div>
                {ed.details && <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, color: "#525252" }}>{ed.details}</div>}
              </div>
            );
          })}
        </Section>
      )}

      {hasAny(projects, ["name"]) && (
        <Section title="Projects">
          {projects.map((p, i) => {
            if (!p.name) return null;
            return (
              <div key={p.id || i} style={{ marginBottom: 6 }}>
                <span data-resume-projectname style={{ fontWeight: 700 }}>{p.name}</span>
                {p.tech && <span style={{ fontStyle: "italic", color: "#525252" }}> — {p.tech}</span>}
                {p.description && <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10 }}>{p.description}</div>}
              </div>
            );
          })}
        </Section>
      )}

      {hasAny(certifications, ["name"]) && (
        <Section title="Certifications">
          {certifications.map((c, i) => c.name && (
            <div key={c.id || i}>
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              {c.issuer && <span style={{ color: "#525252" }}> — {c.issuer}</span>}
              {c.date && <span style={{ fontStyle: "italic", color: "#525252" }}> · {c.date}</span>}
            </div>
          ))}
        </Section>
      )}

      {languages?.some((l) => l.name?.trim()) && (
        <Section title="Languages">
          {languages.map((l) => l.name?.trim() && (
            <div key={l.id}>
              <span style={{ fontWeight: 600 }}>{l.name}</span>
              {l.level && <span style={{ fontStyle: "italic", color: "#525252" }}> — {l.level}</span>}
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 14 }}>
      <div data-resume-h2 style={{ textAlign: "center", fontWeight: 600, fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </section>
  );
}
